import { ReportConfigV2 } from "@/common/report-configuration.ts";
import { DIMENSIONS } from "@/common/dimensions.ts";

export const COLOR_PALETTE = [
  "#0067A5", // Blue
  "#F3C300", // Yellow
  "#875692", // Violet
  "#F38400", // Orange
  "#A1CAF1", // Light Blue
  "#BE0032", // Red
  "#C2B280", // Buff
  "#949492", // Gray
  "#008856", // Green
  "#E68FAC", // Purplish Pink
  "#F99379", // Yellowish Pink
  "#604E97", // Violet
  "#F6A600", // Orange Yellow
  "#B3446C", // Purplish Red
  "#DCD300", // Greenish Yellow
  "#882D17", // Reddish Brown
  "#8DB600", // Yellow Green
  "#654522", // Yellowish Brown
  "#E25822", // Reddish Orange
  "#3B5D36", // Olive Green
] as const;

const GENDER_COLORS = {
  Female: COLOR_PALETTE[5], // red
  Male: COLOR_PALETTE[0], // blue
} as const;

/**
 * Lightens a hex color by a specified amount (default: 40%).
 * @param hex - Hex color string (e.g., "#4BABE8")
 * @param percent - Amount to lighten (0-100, default: 40)
 * @returns Lightened hex color string
 */
export const lightenColor = (hex: string, percent: number = 40): string => {
  // Remove # if present
  const color = hex.replace("#", "");

  // Parse RGB values
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  // Lighten by moving towards white (255)
  const amount = Math.min(Math.max(percent, 0), 100) / 100;
  const newR = Math.round(r + (255 - r) * amount);
  const newG = Math.round(g + (255 - g) * amount);
  const newB = Math.round(b + (255 - b) * amount);

  // Convert back to hex
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

const ENERGY_COLORS = {
  ConsciousBlue: COLOR_PALETTE[0], // Blue
  ConsciousGreen: COLOR_PALETTE[8], // Green
  ConsciousRed: COLOR_PALETTE[5], // Red
  ConsciousYellow: COLOR_PALETTE[1], // Yellow
  LessConsciousBlue: lightenColor(COLOR_PALETTE[0]), // Light Blue
  LessConsciousGreen: lightenColor(COLOR_PALETTE[8]), // Light Green
  LessConsciousRed: lightenColor(COLOR_PALETTE[5]), // Light Red
  LessConsciousYellow: lightenColor(COLOR_PALETTE[1]), // Light Yellow
} as const;

type EnergyColorKey = keyof typeof ENERGY_COLORS;

const ENERGY_COLOR_TOKEN_MAP: Record<
  string,
  "Blue" | "Green" | "Red" | "Yellow"
> = {
  blue: "Blue",
  green: "Green",
  red: "Red",
  yellow: "Yellow",
};

export const getEnergyColorKeyFromText = (
  text: string,
): EnergyColorKey | null => {
  const normalizedText = text.toLowerCase().replace(/[_-]+/g, " ");

  const lessConsciousColorMatch = normalizedText.match(
    /\b(blue|green|red|yellow)\s*lc\b/,
  );
  if (lessConsciousColorMatch) {
    const colorName = ENERGY_COLOR_TOKEN_MAP[lessConsciousColorMatch[1]];
    return `LessConscious${colorName}`;
  }

  const colorMatch = normalizedText.match(/\b(blue|green|red|yellow)\b/);
  if (!colorMatch) return null;

  const colorName = ENERGY_COLOR_TOKEN_MAP[colorMatch[1]];

  // Legacy label support while we transition naming conventions.
  if (/\bless\s*conscious\b/.test(normalizedText)) {
    return `LessConscious${colorName}`;
  }

  // New convention defaults plain color labels to conscious energy.
  return `Conscious${colorName}`;
};

export const resolveEnergyColor = (text: string): string | undefined => {
  const energyColorKey = getEnergyColorKeyFromText(text);
  return energyColorKey ? ENERGY_COLORS[energyColorKey] : undefined;
};

export const resolveCategorySeriesColor = (
  text: string,
): string | undefined => {
  if (text.trim() === "") return COLOR_PALETTE[7]; // Gray for empty category labels
  return resolveEnergyColor(text);
};

export const generateColor = (index: number): string => {
  return COLOR_PALETTE[index % COLOR_PALETTE.length];
};

// Helper function to check if a dimension is gender-related
const isGenderDimension = (dim: string) =>
  dim.toLowerCase() === "gender" || dim.toLowerCase().includes("_gender");

/**
 * Darkens a hex color by a specified amount (default: 40%).
 * @param hex - Hex color string (e.g., "#4BABE8")
 * @param percent - Amount to darken (0-100, default: 40)
 * @returns Darkened hex color string
 */
export const darkenColor = (hex: string, percent: number = 40): string => {
  const color = hex.replace("#", "");

  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  const amount = Math.min(Math.max(percent, 0), 100) / 100;
  const newR = Math.round(r * (1 - amount));
  const newG = Math.round(g * (1 - amount));
  const newB = Math.round(b * (1 - amount));

  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

const LIKERT_COLORS: Record<string, string> = {
  "1": COLOR_PALETTE[5], // red
  "2": COLOR_PALETTE[9], // purplish pink
  "3": COLOR_PALETTE[7], // gray
  "4": lightenColor(COLOR_PALETTE[8]), // light green
  "5": COLOR_PALETTE[8], // green
};

/**
 * Assigns colors to multiple series at once with collision detection.
 * Returns a Map of series key -> color hex string.
 *
 * @param allSeriesKeys - Array of all series keys that need colors
 * @param config - Report configuration (used for gender color detection)
 * @returns Map of series key to color hex string
 */
export const assignSeriesColorsV2 = (
  allSeriesKeys: string[],
  config: ReportConfigV2,
): Map<string, string> => {
  const colorMap = new Map<string, string>();

  // Get all dimensions for gender detection
  const dimensionFields = config.data.dimensions.map(
    (dim) => DIMENSIONS[dim].field,
  );
  const hasLikertScale = dimensionFields[0] === "answer_value";
  const hasGenderDimension = dimensionFields.some((dim) =>
    isGenderDimension(dim),
  );

  // Helper function to compute hash for direct color palette indexing
  const getHash = (key: string): number => {
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }
    // Return absolute value to ensure positive index
    return Math.abs(hash);
  };

  // Separate projected keys from base keys
  const projectedKeys = allSeriesKeys.filter((key) =>
    key.endsWith("_projected"),
  );
  const baseKeys = allSeriesKeys.filter((key) => !key.endsWith("_projected"));

  // Track used color indices for collision detection
  const usedColorIndices = new Set<number>();

  // Special case: if there's only one base key, prefer category-aware semantic colors.
  if (baseKeys.length === 1) {
    colorMap.set(
      baseKeys[0],
      resolveCategorySeriesColor(baseKeys[0]) ?? generateColor(0),
    );
    usedColorIndices.add(0);
  } else {
    // Process base keys

    baseKeys.forEach((key) => {
      const keyLower = key.toLowerCase();

      if (key.trim() === "") {
        colorMap.set(key, COLOR_PALETTE[7]); // Gray for empty nested series labels
        usedColorIndices.add(7);
        return;
      }

      // Check for gender-specific colors first
      if (
        hasGenderDimension &&
        dimensionFields.length <= 2 &&
        config.data.datasetName === "main"
      ) {
        if (keyLower.includes("female")) {
          colorMap.set(key, GENDER_COLORS.Female);
          // Mark index 4 as used (Female color)
          usedColorIndices.add(4);
          return;
        }
        if (keyLower.includes("male")) {
          colorMap.set(key, GENDER_COLORS.Male);
          // Mark index 0 as used (Male color)
          usedColorIndices.add(0);
          return;
        }
      }

      if (hasLikertScale) {
        const likertColor = LIKERT_COLORS[key];
        colorMap.set(key, likertColor);
        return;
      }

      const energyColor = resolveEnergyColor(key);
      if (energyColor) {
        colorMap.set(key, energyColor);
        return;
      }

      // For regular series, use hash to directly index the palette
      const hash = getHash(key);
      const originalColorIndex = hash % COLOR_PALETTE.length;
      let colorIndex = originalColorIndex;

      // Handle collisions: if the color is already used, find the next available one
      let attempts = 0;
      while (
        usedColorIndices.has(colorIndex) &&
        attempts < COLOR_PALETTE.length
      ) {
        colorIndex = (colorIndex + 1) % COLOR_PALETTE.length;
        attempts++;
      }

      // If palette is exhausted, fall back to original hashed index
      if (attempts >= COLOR_PALETTE.length) {
        colorIndex = originalColorIndex;
      }

      colorMap.set(key, generateColor(colorIndex));
      usedColorIndices.add(colorIndex);
    });
  }

  // Handle projected keys by lightening their base key colors
  projectedKeys.forEach((projectedKey) => {
    const baseKey = projectedKey.replace("_projected", "");
    const baseColor = colorMap.get(baseKey);

    if (baseColor) {
      // Lighten the base color for the projected key
      colorMap.set(projectedKey, lightenColor(baseColor));
    } else {
      // Fallback: if no base key exists, use hash to assign a color
      const hash = getHash(projectedKey);
      const originalColorIndex = hash % COLOR_PALETTE.length;
      let colorIndex = originalColorIndex;

      // Handle collisions
      let attempts = 0;
      while (
        usedColorIndices.has(colorIndex) &&
        attempts < COLOR_PALETTE.length
      ) {
        colorIndex = (colorIndex + 1) % COLOR_PALETTE.length;
        attempts++;
      }

      // If palette is exhausted, fall back to original hashed index
      if (attempts >= COLOR_PALETTE.length) {
        colorIndex = originalColorIndex;
      }

      colorMap.set(projectedKey, generateColor(colorIndex));
      usedColorIndices.add(colorIndex);
    }
  });

  return colorMap;
};
